import { useEffect, useMemo, useState } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Link
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  changeAppSourceStatus,
  exportAppSources,
  fetchAppSources
} from '../store/slices/appSourcesSlice';

const statusTabs = [
  { label: 'All', value: 'ALL' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'INACTIVE' }
];

const quotaFilters = [
  { label: 'All Quotas', value: 'ALL' },
  { label: 'Unlimited Only', value: 'UNLIMITED' },
  { label: 'Limited Only', value: 'LIMITED' }
];

const columns = [
  { id: 'name', label: 'Name' },
  { id: 'quota_type', label: 'Quota Type' },
  { id: 'usage_percentage', label: 'Quota Usage (%)' },
  { id: 'usage_quota', label: 'Usage / Quota' },
  { id: 'status', label: 'Status' },
  { id: 'start_datetime', label: 'Start Date' },
  { id: 'end_datetime', label: 'End Date' },
  { id: 'created_datetime', label: 'Created' },
  { id: 'updated_datetime', label: 'Updated' },
  { id: 'actions', label: 'Actions', sortable: false }
];

const getUsagePercentage = (quota, usage) => {
  if (!quota) return null;
  if (quota === 0) return null;
  if (usage === 0) return 0;
  return Math.min(100, Math.round((usage / quota) * 100));
};

const formatDateOnly = (value) => {
  if (!value) return '-';
  if (typeof value === 'string') {
    if (value.includes(' ')) {
      return value.split(' ')[0];
    }
    return value.slice(0, 10);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toISOString().slice(0, 10);
};

const truncateSecretKey = (secret) => {
  if (!secret || secret.length <= 12) return secret;
  const prefix = secret.slice(0, 6);
  const suffix = secret.slice(-4);
  return `${prefix}...${suffix}`;
};

const copyToClipboard = (value) => {
  if (!value) return;
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(value).catch(() => {
      /* ignore clipboard errors */
    });
  }
};

const AppSourceListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    items,
    loading,
    error,
    meta,
    statusUpdates,
    exportStatus,
    exportError,
    lastQuery,
    statusCounts: statusSummary
  } = useSelector((state) => state.appSources);

  const [search, setSearch] = useState(lastQuery?.search ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(lastQuery?.search ?? '');
  const [statusTab, setStatusTab] = useState(lastQuery?.status ?? 'ALL');
  const [quotaFilter, setQuotaFilter] = useState(lastQuery?.quotaFilter ?? 'ALL');
  const [sortBy, setSortBy] = useState(lastQuery?.sortBy ?? 'created_datetime');
  const [sortDirection, setSortDirection] = useState(lastQuery?.sortDirection ?? 'desc');
  const [page, setPage] = useState(Math.max((lastQuery?.page ?? 1) - 1, 0));
  const [rowsPerPage, setRowsPerPage] = useState(lastQuery?.pageSize ?? 10);
  const [selectedItem, setSelectedItem] = useState(null);
  const [dense, setDense] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const openMenu = Boolean(menuAnchor);

  const handleDownload = () => {
    setMenuAnchor(null);
    dispatch(
      exportAppSources({
        page: page + 1,
        pageSize: rowsPerPage,
        search: debouncedSearch.trim(),
        status: statusTab,
        quotaFilter,
        sortBy,
        sortDirection
      })
    );
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    dispatch(
      fetchAppSources({
        page: page + 1,
        pageSize: rowsPerPage,
        search: debouncedSearch.trim(),
        status: statusTab,
        quotaFilter,
        sortBy,
        sortDirection
      })
    );
  }, [dispatch, page, rowsPerPage, debouncedSearch, statusTab, quotaFilter, sortBy, sortDirection]);

  useEffect(() => {
    if (!meta) return;

    if (typeof meta.page === 'number' && meta.page - 1 !== page) {
      setPage(Math.max(meta.page - 1, 0));
    }

    if (typeof meta.page_size === 'number' && meta.page_size !== rowsPerPage) {
      setRowsPerPage(meta.page_size);
    }
  }, [meta, page, rowsPerPage]);

  const statusCounts = useMemo(() => {
    if (statusSummary) {
      return {
        ALL: statusSummary.ALL ?? statusSummary.all ?? 0,
        ACTIVE: statusSummary.ACTIVE ?? statusSummary.active ?? 0,
        INACTIVE: statusSummary.INACTIVE ?? statusSummary.inactive ?? 0
      };
    }

    return items.reduce(
      (acc, item) => {
        const status = item.status;
        acc.ALL += 1;
        if (status === 'ACTIVE') acc.ACTIVE += 1;
        if (status === 'INACTIVE') acc.INACTIVE += 1;
        return acc;
      },
      { ALL: 0, ACTIVE: 0, INACTIVE: 0 }
    );
  }, [items, statusSummary]);

  const handleSort = (columnId) => {
    if (columnId === 'actions') return;
    if (sortBy === columnId) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(columnId);
      setSortDirection('asc');
    }
    setPage(0);
  };

  const handleChangeStatus = (item) => {
    const nextStatus = item.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    dispatch(changeAppSourceStatus({ id: item.id, status: nextStatus }));
  };

  const emptyState = (
    <Box sx={{ textAlign: 'center', py: 5 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        No results found
      </Typography>
      <Typography color="text.secondary">
        Try adjusting your search or filters to find what you need.
      </Typography>
    </Box>
  );

  const detailRows = useMemo(() => {
    if (!selectedItem) return [];

    const formatNumber = (value) => {
      if (value === null || value === undefined) return 'N/A';
      const numeric = Number(value);
      if (Number.isFinite(numeric)) {
        return numeric.toLocaleString();
      }
      return String(value);
    };

    return [
      { label: 'ID', value: selectedItem.id ?? 'N/A' },
      { label: 'Name', value: selectedItem.name ?? 'N/A' },
      { label: 'Callback URL', value: selectedItem.callback_url || 'N/A' },
      { label: 'Status', value: selectedItem.status ?? 'N/A' },
      { label: 'Quota Type', value: selectedItem.quota_type || 'N/A' },
      { label: 'Secret Key', type: 'secret', value: selectedItem.secret_key },
      { label: 'Quota', value: formatNumber(selectedItem.quota) },
      { label: 'Quota Usage', value: formatNumber(selectedItem.quota_usage) },
      { label: 'Start Date', value: formatDateOnly(selectedItem.start_datetime) },
      { label: 'End Date', value: formatDateOnly(selectedItem.end_datetime) },
      { label: 'Created', value: selectedItem.created_datetime || 'N/A' },
      { label: 'Updated', value: selectedItem.updated_datetime || 'N/A' }
    ];
  }, [selectedItem]);

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="/"
            sx={{ fontSize: 12, mb: 1, color: 'text.secondary' }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="#">
              Management
            </Link>
            <Typography color="text.primary">App Sources</Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            App Sources
          </Typography>
          <Typography color="text.secondary">
            Manage application sources, quotas, and statuses.
          </Typography>
        </Box>
        <Button
          size="large"
          onClick={() => navigate('/app-sources/new')}
          sx={{
            alignSelf: { xs: 'stretch', md: 'center' },
            width: { xs: '100%', sm: 'auto' },
            maxWidth: { xs: 'calc(100vw - 32px)', sm: 220 },
            boxSizing: 'border-box'
          }}
        >
          + New App Source
        </Button>
      </Box>

      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)'
        }}
      >
        <Stack spacing={2.5}>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              overflowX: 'auto',
              overflowY: 'hidden',
              py: 0.5,
              px: 0.5,
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { 
                display: 'none' 
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(148, 163, 184, 0.35)',
                borderRadius: 999
              }
            }}
          >
            {statusTabs.map((tab) => {
              const isActive = statusTab === tab.value;
              return (
                <Chip
                  key={tab.value}
                  label={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography component="span" fontWeight={600}>
                        {tab.label}
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 24,
                          height: 20,
                          borderRadius: 999,
                          backgroundColor: isActive
                            ? 'rgba(255,255,255,0.24)'
                            : 'rgba(15, 23, 42, 0.06)',
                          color: isActive ? '#ffffff' : 'text.secondary',
                          px: 1,
                          fontSize: 12,
                          fontWeight: 600
                        }}
                      >
                        {statusCounts[tab.value]}
                      </Box>
                    </Box>
                  }
                  color={isActive ? 'primary' : 'default'}
                  variant={isActive ? 'filled' : 'outlined'}
                  onClick={() => {
                    setPage(0);
                    setStatusTab(tab.value);
                  }}
                  sx={{
                    borderRadius: 10,
                    height: 38,
                    fontWeight: 600,
                    color: isActive ? theme.palette.primary.contrastText : 'text.secondary'
                  }}
                />
              );
            })}
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ md: 'center' }}
          >
            <FormControl
              sx={{
                width: { xs: '100%', md: 220 },
                maxWidth: { xs: 'calc(100vw - 32px)', md: 220 },
                boxSizing: 'border-box',
                flexShrink: 0
              }}
            >
              <InputLabel>Quota</InputLabel>
              <Select
                value={quotaFilter}
                label="Quota"
                onChange={(event) => {
                  setPage(0);
                  setQuotaFilter(event.target.value);
                }}
              >
                {quotaFilters.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 2,
                width: { xs: '100%', md: 'auto' },
                maxWidth: { xs: 'calc(100vw - 32px)', md: '100%' },
                flexGrow: 1,
                boxSizing: 'border-box'
              }}
            >
              <TextField
                value={search}
                onChange={(event) => {
                  setPage(0);
                  setSearch(event.target.value);
                }}
                placeholder="Search by name or secret key"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  )
                }}
                sx={{
                  flexGrow: 1,
                  width: '100%',
                  boxSizing: 'border-box',
                  minWidth: 0
                }}
              />
              <IconButton
                onClick={(event) => setMenuAnchor(event.currentTarget)}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  width: 48,
                  height: 48,
                  flexShrink: 0
                }}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={openMenu}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  elevation: 6,
                  sx: {
                    boxShadow: '0px 22px 45px rgba(15, 23, 42, 0.18)',
                    border: '1px solid rgba(148, 163, 184, 0.16)',
                    background:
                      'radial-gradient(circle at top right, rgba(14, 165, 233, 0.24), transparent 55%), #ffffff',
                    borderRadius: 1,
                    mt: 1
                  }
                }}
              >
                <MenuItem onClick={handleDownload} disabled={exportStatus === 'loading'}>
                  <DownloadIcon fontSize="small" style={{ marginRight: 8 }} />
                  {exportStatus === 'loading' ? 'Preparing...' : 'Download Excel'}
                </MenuItem>
              </Menu>
            </Box>
          </Stack>
        </Stack>

        {error && !loading && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
                        )}
        {exportError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {exportError}
          </Alert>
                        )}

        {loading && <LinearProgress sx={{ mt: 3 }} />}

        <Box
          sx={{
            width: '100%',
            overflowX: 'auto',
            border: '1px solid rgba(148, 163, 184, 0.16)',
            borderRadius: 1,
            mt: 3,
            backgroundColor: 'background.paper',
            '&::-webkit-scrollbar': {
              height: 6
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(148, 163, 184, 0.35)',
              borderRadius: 999
            }
          }}
        >
          <TableContainer
            sx={{
              display: 'inline-block',              
              minWidth: '100%' ,
              width: '100%'
            }}
          >
            <Table
              size={dense ? 'small' : 'medium'}
              sx={{ minWidth: 1020, borderCollapse: 'separate', borderSpacing: 0 }}
            >
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      sortDirection={sortBy === column.id ? sortDirection : false}
                    >
                      {column.sortable === false ? (
                        column.label
                        ) : (
                        <TableSortLabel
                          active={sortBy === column.id}
                          direction={sortBy === column.id ? sortDirection : 'asc'}
                          onClick={() => handleSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                        )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
              {items.map((item) => {
                const usagePercentage = getUsagePercentage(item.quota, item.quota_usage);
                const usageQuotaLabel =
                  item.quota === 0
                    ? `${item.quota_usage.toLocaleString()} / Unlimited`
                    : `${item.quota_usage.toLocaleString()} / ${item.quota.toLocaleString()}`;

                const usageChip =
                  usagePercentage === null ? (
                    <Chip label="Unlimited" size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                  ) : (
                    <Chip
                      label={`${usagePercentage}%`}
                      size="small"
                      color={usagePercentage >= 100 ? 'error' : usagePercentage >= 80 ? 'warning' : 'success'}
                      sx={{ fontWeight: 600 }}
                    />
                  );

                return (
                  <TableRow
                    hover
                    key={item.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(15, 23, 42, 0.02)'
                      }
                    }}
                  >
                    <TableCell sx={{ minWidth: 220 }}>
                      <Typography id={`app-source-${item.id}`} fontWeight={600}>
                        {item.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        #{item.id}
                        {item.secret_key ? (
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}>
                            &bull; {truncateSecretKey(item.secret_key)}
                            <Tooltip title="Copy secret key">
                              <IconButton
                                size="small"
                                sx={{ ml: 0.75 }}
                                onClick={() => copyToClipboard(item.secret_key)}
                              >
                                <ContentCopyIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          'No secret key'
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.quota_type || '-'}</TableCell>
                    <TableCell sx={{ textAlign: 'left' }}>{usageChip}</TableCell>
                    <TableCell sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {usageQuotaLabel}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.status}
                        color={item.status === 'ACTIVE' ? 'success' : 'default'}
                        variant={item.status === 'ACTIVE' ? 'filled' : 'outlined'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDateOnly(item.start_datetime)}</TableCell>
                    <TableCell>{formatDateOnly(item.end_datetime)}</TableCell>
                    <TableCell>{item.created_datetime}</TableCell>
                    <TableCell>{item.updated_datetime || 'N/A'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <IconButton color="primary" onClick={() => setSelectedItem(item)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          color="primary"
                          onClick={() => navigate(`/app-sources/${item.id}/edit`)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          color={item.status === 'ACTIVE' ? 'success' : 'default'}
                          disabled={statusUpdates?.[item.id] === 'loading'}
                          onClick={() => handleChangeStatus(item)}
                        >
                          {item.status === 'ACTIVE' ? (
                            <ToggleOnIcon fontSize="small" />
                          ) : (
                            <ToggleOffIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!items.length && !loading && (
                <TableRow>
                  <TableCell colSpan={columns.length}>{emptyState}</TableCell>
                </TableRow>
              )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <FormControlLabel
            control={<Switch checked={dense} onChange={(event) => setDense(event.target.checked)} />}
            label="Dense"
          />
          <TablePagination
            component="div"
            count={meta?.total_items ?? 0}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              const nextRows = parseInt(event.target.value, 10);
              setRowsPerPage(nextRows);
              setPage(0);
            }}
            sx={{
              '& .MuiTablePagination-toolbar': {
                px: { xs: 0, sm: 2 }
              }
            }}
          />
        </Stack>
      </Paper>

      <Dialog
        open={Boolean(selectedItem)}
        onClose={() => setSelectedItem(null)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>App Source Details</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {detailRows.map((row) => (
              <Box key={row.label}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase' }}
                >
                  {row.label}
                </Typography>
                {row.type === 'secret' ? (
                  row.value ? (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ wordBreak: 'break-all' }}
                    >
                      <Typography variant="subtitle1">{row.value}</Typography>
                      <Tooltip title="Copy secret key">
                        <IconButton size="small" onClick={() => copyToClipboard(row.value)}>
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Typography variant="subtitle1">No secret key</Typography>
                  )
                ) : (
                  <Typography variant="subtitle1">{row.value ?? 'N/A'}</Typography>
                )}
              </Box>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </Stack>
  );
};

export default AppSourceListPage;



